// Columna derecha del explorer (`.col-detail` del mockup): hero con «Comprobante» y cinco pestañas
// (Venta · Producto · Cliente · Comisión · Bitácora) sobre la venta que la tabla ya trajo.
//
// No hay una segunda llamada de detalle: `GET /sales/{id}` devuelve el MISMO DTO que el ítem del
// listado, así que la ficha se arma de la fila, igual que el mockup la arma de su lista en memoria.
import type { ReactNode } from 'react';
import { FileInput, Receipt, User } from 'lucide-react';
import { Badge, CopyableId, ErrorState, Skeleton } from '@/kit';
import { MasterDetailEmpty } from '@/shared/patterns/MasterDetail';
import { SaleReceiptButton, type SaleSlots } from '@/shared/patterns/SaleReceipt';
import { StatusPill, TierPill } from '@/shared/patterns/Pills';
import { cn } from '@/lib/cn';
import { formatDateMx, formatDateTimeMx } from '@/lib/dateHelpers';
import { formatMXN } from '@/shared/lib/format/money';
import { saleCustomerLabel } from '@/shared/lib/format/saleCustomer';
import { hasEquipment } from '@/shared/patterns/SaleTicket';
import type { Customer360 } from '@/api/services/retail-customer';
import { LoanDeviceMdmStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanDeviceMdmStatusEnum';
import type { RecentSale } from '../../api/usePortal';
import { CommissionAwaitingBadge } from '../../components/CommissionAwaitingBadge';
import { SaleStatusBadge } from '../../components/SaleStatusBadge';
import { PortalCancelSaleAction } from './PortalCancelSaleAction';
import { financierChipOf, sellerLabel } from '../../store/lib/storeFormat';
import type { CustomerCreditFacts } from '../../customers/lib/customerCreditFacts';
import { CREDIT_ELSEWHERE_LABEL, formatPhone } from '../../customers/lib/customersFormat';
import { creditUnreadNote } from '../../prospects/lib/prospectsFormat';
import {
    type CreditCardTone,
    creditHistoryCards,
    creditTierFacts,
    SALE_WITHOUT_CUSTOMER_NOTE,
} from '../lib/saleCustomerCredit';
import { saleCatalogFacts, saleCategoryLabel, type CatalogState } from '../lib/mySalesCatalog';
import type { SaleDeviceState } from '../api/useSaleDevice';
import {
    DEVICE_NOT_APPLICABLE_NOTE,
    DEVICE_PENDING_ENROLLMENT_NOTE,
    DEVICE_UNREADABLE_NOTE,
    deviceLockStatusLabel,
    deviceProviderLabel,
} from '../lib/saleDeviceFormat';
import { weekLabel } from '@/domains/retail/admin/comisiones/lib/payoutPeriods';
import { weeklyCutFor } from '../lib/mySalesCommission';
import {
    CREDIT_PRICE_NOTE,
    financierKeyOf,
    financierName,
    folioTail,
    PENDING_CELL,
    saleAlert,
    saleTypeLabel,
    showsSaleLines,
    UNUSABLE_DATE_NOTE,
    type AlertTone,
} from '../lib/mySalesFormat';
import { buildSaleTimeline } from '../lib/mySalesTimeline';
import {
    DETAIL_SECTION_TITLE as SECTION_TITLE,
    DetailRow as InfoRow,
    PendingDetailRow as PendingRow,
    SaleAmountsSection,
    SaleFactsSection,
    SaleLinesSection,
    SalePaymentSection,
    SoldEquipmentSection,
    salePaymentSummary,
    type SaleDetailFacts,
} from './SaleDetailSections';

export const DETAIL_TABS = ['venta', 'producto', 'cliente', 'comision', 'bitacora'] as const;
export type SaleDetailTab = (typeof DETAIL_TABS)[number];

const TAB_LABEL: Record<SaleDetailTab, string> = {
    venta: 'Venta',
    producto: 'Producto',
    cliente: 'Cliente',
    comision: 'Comisión',
    bitacora: 'Bitácora',
};

/** Chips del hero a 9.5px, como los `chip text-[9.5px]` del mock. */
const HERO_CHIP = 'px-2 py-0.5 text-[9.5px] font-medium';

/** Título de la primera sección de cada pestaña. */
const FIRST_TITLE = 'mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground';

/** Nota bajo una fila o dentro del cajón del dispositivo. */
const NOTE_CLASS = 'text-[11px] leading-relaxed text-muted-foreground';

/** `.btn-comprobante` del mock. */
const RECEIPT_BUTTON =
    'inline-flex flex-none items-center gap-[5px] rounded-[5px] border border-border bg-card px-2.5 py-1 text-[11px] font-semibold text-foreground transition-colors hover:border-primary/50 hover:bg-accent/50 hover:text-primary';

const ALERT_CLASS: Record<AlertTone, string> = {
    info: 'bg-info-soft text-info',
    success: 'bg-success-soft text-success',
    warning: 'bg-warning-soft text-warning',
    danger: 'bg-danger-soft text-danger',
};

const CARD_TONE_CLASS: Record<CreditCardTone, string> = {
    neutral: '',
    success: 'text-success',
    danger: 'text-danger',
};

/** El Cliente 360 del cliente de la venta, tal como lo devuelve `useCustomerDetail`. */
export interface CustomerDetailState {
    data: Customer360 | undefined;
    isLoading: boolean;
    error: Error | null;
}

export function SaleDetailPanel({
    sale,
    customer,
    credit,
    catalog,
    device,
    onRetryDevice,
    tab,
    onTabChange,
    onOpenCustomer,
    notFound = false,
    onRetryLink,
    slots = {},
}: {
    sale: RecentSale | null;
    customer: CustomerDetailState;
    /** Las cifras de crédito del cliente, o el motivo por el que no las hay. Nunca las dos. */
    credit: CustomerCreditFacts;
    /** El catálogo de la tienda, del que la pestaña Producto saca la ficha actual del equipo. */
    catalog: CatalogState;
    /** Expediente MDM del aparato vendido, la fuente del cajón «Dispositivo». */
    device: SaleDeviceState;
    /** Vuelve a pedir el expediente cuando su lectura falló. */
    onRetryDevice: () => void;
    tab: SaleDetailTab;
    onTabChange: (tab: SaleDetailTab) => void;
    onOpenCustomer: (retailCustomerId: string) => void;
    /** El `?sale=` de la URL no está en la tabla: se dice, no se abre la ficha de otra venta. */
    notFound?: boolean;
    /** La venta del enlace no se pudo leer (no es «no existe»): se ofrece reintentar. */
    onRetryLink?: () => void;
    /** Piezas de Promociones en las filas de línea y al pie; vacío se ve igual que sin promos. */
    slots?: SaleSlots;
}) {
    if (notFound) {
        return (
            <MasterDetailEmpty
                icon={<Receipt className="size-8" aria-hidden />}
                title="No se encontró esa venta"
                sub="El enlace apunta a una venta que no está en tu listado. Búscala por su folio o por el nombre del cliente."
            />
        );
    }

    if (onRetryLink) {
        return (
            <ErrorState
                className="m-3"
                title="No se pudo leer la venta del enlace."
                description="Vuelve a intentarlo; si sigue igual, avísale a tu gerente."
                onRetry={onRetryLink}
            />
        );
    }

    if (!sale) {
        return (
            <MasterDetailEmpty
                icon={<Receipt className="size-8" aria-hidden />}
                title="Selecciona una venta"
                sub="Elige una fila de la tabla para ver su detalle, el equipo que se entregó y su corte de comisión."
            />
        );
    }

    const chip = financierChipOf(sale.type, sale.externalProvider);
    const financier = financierName(financierKeyOf(sale.type, sale.externalProvider));
    const seller = sellerLabel(sale.sellerName, sale.sellerRetailUserId);

    return (
        <>
            {/* .detail-hero */}
            <div className="flex-none border-b border-border px-3.5 py-3">
                <div className="mb-1 flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <div className="text-[9.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Venta
                        </div>
                        <Badge variant="muted" className="px-2 py-0.5 text-[9px] font-medium">
                            {sale.atTime}
                        </Badge>
                    </div>
                    <SaleReceiptButton
                        sale={{
                            saleId: sale.saleId,
                            folio: sale.saleRef,
                            soldAt: sale.soldAt,
                            status: sale.status,
                            customerName: saleCustomerLabel(sale),
                            storeName: sale.storeName,
                            sellerName: sale.sellerName,
                            lines: sale.lines,
                            amountCents: sale.amountCents,
                            paymentSummary: salePaymentSummary({
                                type: sale.type,
                                externalProvider: sale.externalProvider,
                                paymentMethod: sale.method,
                            }),
                            ...(sale.salePromotionId !== undefined && {
                                salePromotionId: sale.salePromotionId,
                            }),
                            ...(sale.salePromotionVersion !== undefined && {
                                salePromotionVersion: sale.salePromotionVersion,
                            }),
                        }}
                        slots={slots}
                        icon={<FileInput className="size-3" aria-hidden />}
                        variant="ticket"
                        className={RECEIPT_BUTTON}
                    />
                </div>
                {/* El folio entero no cabe en una línea: va el tramo que lo distingue y el resto en el title. */}
                <div
                    title={sale.saleRef}
                    className="truncate font-mono text-[15px] font-semibold leading-tight"
                >
                    {folioTail(sale.saleRef)}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <SaleStatusBadge status={sale.status} className={HERO_CHIP} />
                    <CommissionAwaitingBadge sale={sale} className={HERO_CHIP} />
                    <Badge variant={chip.variant} className={HERO_CHIP}>
                        {financier}
                    </Badge>
                    <Badge variant="muted" className={HERO_CHIP}>
                        {saleTypeLabel(sale.type)}
                    </Badge>
                </div>
                <div className="mt-2 flex min-w-0 items-center gap-1.5 text-[11.5px]">
                    <User className="size-3 flex-none text-muted-foreground" aria-hidden />
                    <span
                        className={cn(
                            'flex-none font-medium',
                            !sale.customerName && 'text-muted-foreground',
                        )}
                    >
                        {saleCustomerLabel(sale) ?? 'Cliente sin nombre en la venta'}
                    </span>
                    <span className="text-muted-foreground">·</span>
                    <span className="flex-none text-[10.5px] text-muted-foreground">
                        {sale.storeName ?? PENDING_CELL}
                    </span>
                    <span className="text-muted-foreground">·</span>
                    <span title={seller} className="truncate text-[10.5px] text-muted-foreground">
                        Vendedor {seller}
                    </span>
                </div>
            </div>

            {/* .detail-tabs */}
            <div
                role="tablist"
                className="flex flex-none gap-0.5 overflow-x-auto border-b border-border px-2 py-1.5"
            >
                {DETAIL_TABS.map((key) => (
                    <button
                        key={key}
                        type="button"
                        role="tab"
                        aria-selected={tab === key}
                        onClick={() => {
                            onTabChange(key);
                        }}
                        className={cn(
                            'whitespace-nowrap rounded-[5px] px-2.5 py-[5px] text-[11.5px] transition-colors',
                            tab === key
                                ? 'bg-accent font-semibold text-foreground'
                                : 'font-medium text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                        )}
                    >
                        {TAB_LABEL[key]}
                    </button>
                ))}
            </div>

            <div className="min-h-0 flex-1 overflow-auto px-3.5 py-3">
                {tab === 'venta' ? (
                    <SaleTab sale={sale} slots={slots} />
                ) : tab === 'producto' ? (
                    <ProductTab
                        sale={sale}
                        catalog={catalog}
                        device={device}
                        onRetryDevice={onRetryDevice}
                        slots={slots}
                    />
                ) : tab === 'cliente' ? (
                    <CustomerTab
                        sale={sale}
                        customer={customer}
                        credit={credit}
                        onOpenCustomer={onOpenCustomer}
                    />
                ) : tab === 'comision' ? (
                    <CommissionTab sale={sale} />
                ) : (
                    <TimelineTab sale={sale} />
                )}
            </div>
        </>
    );
}

/** Recuadro de aviso al pie de una pestaña, con el tono que le toque. */
function AlertBox({ tone, children }: { tone: AlertTone; children: ReactNode }) {
    return (
        <p
            className={cn(
                'mt-3 rounded-md px-2.5 py-2 text-[11px] leading-relaxed',
                ALERT_CLASS[tone],
            )}
        >
            {children}
        </p>
    );
}

/** Venta ya cerrada en el vocabulario del detalle compartido con Ventas del Admin VL. */
function detailFactsOf(sale: RecentSale): SaleDetailFacts {
    return {
        saleId: sale.saleId,
        folio: sale.saleRef,
        soldAt: sale.soldAt,
        storeName: sale.storeName,
        zoneName: sale.zoneName,
        sellerLabel: sellerLabel(sale.sellerName, sale.sellerRetailUserId),
        type: sale.type,
        externalProvider: sale.externalProvider,
        creditId: sale.creditId,
        paymentMethod: sale.method,
        payments: sale.payments,
        ...(sale.paymentDetail?.cashConfirmedInRegister !== undefined && {
            cashConfirmed: sale.paymentDetail.cashConfirmedInRegister,
        }),
        amountCents: sale.amountCents,
        downPaymentCents: sale.downPaymentCents,
        equipmentPriceCents: sale.equipmentPriceCents,
        ...(sale.lines !== undefined && { lines: sale.lines }),
        sku: sale.sku,
        imei: sale.imei,
        productBrand: sale.productBrand,
        productModel: sale.productModel,
        productCapacity: sale.productCapacity,
        ...(sale.salePromotionId !== undefined && { salePromotionId: sale.salePromotionId }),
        ...(sale.salePromotionVersion !== undefined && {
            salePromotionVersion: sale.salePromotionVersion,
        }),
    };
}

function SaleTab({ sale, slots }: { sale: RecentSale; slots: SaleSlots }) {
    const alert = saleAlert(sale);
    const facts = detailFactsOf(sale);
    return (
        <>
            <SaleFactsSection sale={facts} />
            <SalePaymentSection sale={facts} />
            <SaleAmountsSection sale={facts} />
            <AlertBox tone={alert.tone}>{alert.text}</AlertBox>
            <PortalCancelSaleAction sale={sale} slots={slots} />
        </>
    );
}

/** Tono del chip del candado: activo en verde, bloqueado en rojo, el resto apagado. */
function lockChipVariant(status: string | undefined): 'success' | 'danger' | 'muted' {
    if (status === LoanDeviceMdmStatusEnum.ACTIVE) return 'success';
    if (status === LoanDeviceMdmStatusEnum.LOCKED) return 'danger';
    return 'muted';
}

/** Estado del candado y proveedor con el que se enroló el aparato vendido. */
function DeviceExpedient({ device, onRetry }: { device: SaleDeviceState; onRetry: () => void }) {
    if (device.status === 'error') {
        return (
            <ErrorState
                className="mt-2 px-3 py-4"
                title="No se pudo leer el candado"
                description={DEVICE_UNREADABLE_NOTE}
                onRetry={onRetry}
            />
        );
    }

    if (device.status !== 'ready') {
        return (
            <div className="mt-2">
                <Skeleton lines={2} />
            </div>
        );
    }

    const { availability, device: expedient } = device.data;
    if (availability === 'NOT_APPLICABLE') {
        return <p className={cn(NOTE_CLASS, 'mt-1')}>{DEVICE_NOT_APPLICABLE_NOTE}</p>;
    }
    if (availability !== 'AVAILABLE' || !expedient) {
        return <p className={cn(NOTE_CLASS, 'mt-1')}>{DEVICE_PENDING_ENROLLMENT_NOTE}</p>;
    }

    return (
        <>
            <InfoRow
                label="MDM candado"
                value={
                    <Badge
                        variant={lockChipVariant(expedient.mdmStatus)}
                        size="xs"
                        className="text-[9px]"
                    >
                        {deviceLockStatusLabel(expedient.mdmStatus)}
                    </Badge>
                }
            />
            <InfoRow label="Proveedor MDM" value={deviceProviderLabel(expedient.mdmProvider)} />
        </>
    );
}

/** El equipo como quedó en la venta; las líneas solo cuando traen accesorios, regalos o promos. */
function ProductTab({
    sale,
    catalog,
    device,
    onRetryDevice,
    slots,
}: {
    sale: RecentSale;
    catalog: CatalogState;
    device: SaleDeviceState;
    onRetryDevice: () => void;
    slots: SaleSlots;
}) {
    const detail = detailFactsOf(sale);
    // Sin teléfono no hay equipo, precio de equipo ni IMEI: solo sus productos.
    if (!hasEquipment(sale)) {
        return <SaleLinesSection sale={detail} slots={slots} />;
    }
    const facts = saleCatalogFacts(sale.sku, catalog);
    const categoryLabel = saleCategoryLabel(sale.sku, sale.lines, catalog);

    return (
        <>
            {showsSaleLines(sale) ? <SaleLinesSection sale={detail} slots={slots} /> : null}
            <SoldEquipmentSection sale={detail}>
                {categoryLabel !== null || facts.status === 'found' ? (
                    <InfoRow label="Categoría" value={categoryLabel ?? PENDING_CELL} />
                ) : (
                    <PendingRow label="Categoría" reason={facts.reason} />
                )}
            </SoldEquipmentSection>

            <div className={SECTION_TITLE}>Precios</div>
            <InfoRow
                label="Precio contado"
                value={
                    sale.equipmentPriceCents === null
                        ? PENDING_CELL
                        : formatMXN(sale.equipmentPriceCents / 100)
                }
            />
            <PendingRow label="Precio crédito" reason={CREDIT_PRICE_NOTE} />

            <div className={SECTION_TITLE}>Dispositivo (IMEI)</div>
            <div className="rounded-md border border-border p-2">
                {sale.imei ? (
                    <>
                        <InfoRow
                            label="IMEI"
                            value={<span className="font-mono">{sale.imei}</span>}
                        />
                        <DeviceExpedient device={device} onRetry={onRetryDevice} />
                    </>
                ) : (
                    <p className={NOTE_CLASS}>
                        Esta venta no registró IMEI. Sin IMEI no hay equipo identificado que ligar
                        al candado remoto.
                    </p>
                )}
            </div>

            {/* La regla es del crédito propio: en contado y en la derivación no aplica. */}
            {sale.type === 'CREDIT' ? (
                <AlertBox tone="info">
                    Todo equipo vendido con crédito SureKeep queda registrado con IMEI y candado
                    remoto.
                </AlertBox>
            ) : null}
        </>
    );
}

function CustomerTab({
    sale,
    customer,
    credit,
    onOpenCustomer,
}: {
    sale: RecentSale;
    customer: CustomerDetailState;
    credit: CustomerCreditFacts;
    onOpenCustomer: (retailCustomerId: string) => void;
}) {
    const retailCustomerId = sale.retailCustomerId;
    // El nombre viene en la venta; el 360 completa teléfono y alta, y si falla se degradan esos dos.
    const detail = customer.data?.customer;
    const missingReason = !retailCustomerId
        ? SALE_WITHOUT_CUSTOMER_NOTE
        : customer.error
          ? 'No se pudo leer la ficha del cliente'
          : null;

    return (
        <>
            <div className={FIRST_TITLE}>Identificación</div>
            <InfoRow label="Nombre" value={saleCustomerLabel(sale) ?? PENDING_CELL} />
            {missingReason !== null ? (
                <>
                    <PendingRow label="Teléfono" reason={missingReason} />
                    <PendingRow label="Alta cliente" reason={missingReason} />
                    <p className={cn(NOTE_CLASS, 'mt-1')}>
                        {retailCustomerId ? (
                            <>
                                <span className="font-semibold">{missingReason}</span>. Teléfono y
                                alta salen de ella; lo demás viene en la venta.
                            </>
                        ) : (
                            missingReason
                        )}
                    </p>
                </>
            ) : customer.isLoading || !detail ? (
                <Skeleton lines={2} className="mt-1" />
            ) : (
                <>
                    <InfoRow label="Teléfono" value={formatPhone(detail.phone)} />
                    <InfoRow
                        label="Alta cliente"
                        value={detail.createdAt ? formatDateMx(detail.createdAt) : PENDING_CELL}
                    />
                </>
            )}

            {retailCustomerId ? (
                <>
                    <CustomerCreditSection facts={credit} />
                    <button
                        type="button"
                        onClick={() => {
                            onOpenCustomer(retailCustomerId);
                        }}
                        className="text-[11.5px] text-primary hover:underline"
                    >
                        Ver ficha completa →
                    </button>
                </>
            ) : null}
        </>
    );
}

/**
 * Nivel SureKeep e historial del cliente de la venta. El nivel es el de CRÉDITO —bronce, plata,
 * oro—; sin lectura de cartera va en guion con su motivo, nunca en cero.
 */
function CustomerCreditSection({ facts }: { facts: CustomerCreditFacts }) {
    if (facts.unreadState === 'loading') {
        return (
            <>
                <div className={SECTION_TITLE}>Nivel SureKeep</div>
                <Skeleton lines={1} />
                <div className={SECTION_TITLE}>Historial con SureKeep</div>
                <Skeleton className="h-12" />
            </>
        );
    }

    const note = facts.entry ? null : creditUnreadNote(facts.unreadState);
    const tier = facts.entry ? creditTierFacts(facts.entry) : null;

    return (
        <>
            <div className={SECTION_TITLE}>Nivel SureKeep</div>
            <div className="mb-3 flex items-center gap-2">
                {tier ? (
                    <TierPill label="full" level={tier.level} title={tier.note} />
                ) : (
                    <span title={note ?? undefined} className="text-muted-foreground">
                        {PENDING_CELL}
                    </span>
                )}
                {facts.entry?.hasActiveCreditElsewhere ? (
                    <StatusPill tone="info">{CREDIT_ELSEWHERE_LABEL}</StatusPill>
                ) : null}
            </div>

            <div className={FIRST_TITLE}>Historial con SureKeep</div>
            <div className="mb-3 grid grid-cols-3 gap-2">
                {creditHistoryCards(facts.entry).map((card) => (
                    <div
                        key={card.label}
                        title={card.value === null ? (note ?? undefined) : undefined}
                        className="rounded-md border border-border p-2 text-center"
                    >
                        <div className="text-[9.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                            {card.label}
                        </div>
                        <div
                            className={cn(
                                'text-[14px] font-semibold tabular-nums',
                                card.value === null
                                    ? 'text-muted-foreground'
                                    : CARD_TONE_CLASS[card.tone],
                            )}
                        >
                            {card.value ?? PENDING_CELL}
                        </div>
                    </div>
                ))}
            </div>
            {note !== null ? <p className={cn(NOTE_CLASS, 'mb-3')}>{note}</p> : null}
        </>
    );
}

/** Sin endpoint de comisión por venta: el corte sí se calcula, el monto se consulta en Mis comisiones. */
function CommissionTab({ sale }: { sale: RecentSale }) {
    const cut = weeklyCutFor(sale.soldAt);

    return (
        <>
            <div className={FIRST_TITLE}>Comisión del vendedor</div>
            <InfoRow
                label="Vendedor"
                value={sellerLabel(sale.sellerName, sale.sellerRetailUserId)}
            />

            <div className={SECTION_TITLE}>Corte semanal</div>
            {cut ? (
                <InfoRow label="Corte" value={weekLabel(cut)} />
            ) : (
                <PendingRow label="Corte" reason={UNUSABLE_DATE_NOTE} />
            )}

            <AlertBox tone="info">
                Las comisiones se calculan con las reglas que configura el Admin de VentasLuga. Tus
                comisiones de la semana se ven en Mis comisiones.
            </AlertBox>
        </>
    );
}

const TIMELINE_DOT: Record<AlertTone, string> = {
    info: 'bg-info',
    success: 'bg-success',
    warning: 'bg-warning',
    danger: 'bg-danger',
};

function TimelineTab({ sale }: { sale: RecentSale }) {
    const events = buildSaleTimeline(sale);

    return (
        <>
            <div className="mb-2 text-[11.5px] font-semibold">Eventos de la venta</div>
            <ul className="mb-3">
                {events.map((event) => (
                    <li
                        key={`${event.at}-${event.text}`}
                        className="flex gap-2 border-b border-border py-1.5 last:border-b-0"
                    >
                        <span
                            className={cn(
                                'mt-[5px] size-1.5 flex-none rounded-full',
                                TIMELINE_DOT[event.tone],
                            )}
                            aria-hidden
                        />
                        <div className="min-w-0 flex-1">
                            <div className="text-[11.5px] leading-snug">{event.text}</div>
                            <div className="font-mono text-[10px] text-muted-foreground">
                                {formatDateTimeMx(event.at)}
                            </div>
                        </div>
                    </li>
                ))}
            </ul>

            <div className={FIRST_TITLE}>Referencias cruzadas</div>
            <div className="flex flex-col gap-1 text-[11.5px]">
                <div>
                    <span className="text-muted-foreground">Cliente: </span>
                    {saleCustomerLabel(sale) ?? (
                        <span className="text-muted-foreground">{PENDING_CELL}</span>
                    )}
                </div>
                {sale.creditId ? <CopyableId label="Crédito" value={sale.creditId} /> : null}
            </div>
        </>
    );
}
